import React, { useMemo } from 'react';
import { Column } from 'react-table';
import styles from './ClientTopPostsTable.module.css';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import Skeleton from 'react-loading-skeleton';
import { buildTiktokPostUrl, numberWithCommas } from '../../../../../../../helpers';
import TruncatedTextCell from '../../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import { ClientTiktokTopVideo } from '@round/api';
import CountryCell from '../../../../../../../SharedComponents/TableComponents/CountryCell/CountryCell';

type TopPostsTableProps = {
    rows: ClientTiktokTopVideo[];
    page: number;
    pageSize: number;
    count: number;
    onPageSizeChange: (pageSize: number | undefined) => void;
    onPageChange: (page: number) => void;
    loading: boolean;
    noDataLabel?: string;
} & Pick<PaginationTableProps<ClientTiktokTopVideo>, 'onOrderByChange' | 'orderBy'>;

const ClientTopPostsTable = ({
    page,
    onPageChange,
    onPageSizeChange,
    onOrderByChange,
    noDataLabel,
    pageSize,
    rows,
    count,
    loading,
    orderBy,
}: TopPostsTableProps) => {
    const columns: Column<ClientTiktokTopVideo>[] = useMemo(
        () => [
            {
                Header: 'Username',
                accessor: 'author_unique_id',
                disableSortBy: true,
                Cell: ({ value }) => (loading ? <Skeleton /> : <TruncatedTextCell>{value}</TruncatedTextCell>),
            },
            {
                Header: 'Followers',
                accessor: 'author_follower_count',
                sortType: 'author_follower_count',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{typeof value !== 'number' ? '-' : numberWithCommas(value)}</>;
                },
            },
            {
                Header: 'Location',
                accessor: 'location',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <CountryCell countryCode={value} />;
                },
            },
            {
                Header: 'Description',
                accessor: 'description',
                disableSortBy: true,
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a
                            className={styles.description}
                            href={buildTiktokPostUrl(original.author_unique_id, original.video_tiktok_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <TruncatedTextCell>{value || 'No Caption'}</TruncatedTextCell>
                        </a>
                    );
                },
            },
            {
                Header: 'Date posted',
                accessor: 'create_time',
                sortType: 'create_time',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : new Date(value * 1000).toLocaleDateString('en-GB')}</>,
            },
            {
                Header: 'Boosted',
                accessor: 'is_ad',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{value ? 'Yes' : 'No'}</>;
                },
            },
            {
                Header: 'Views',
                accessor: 'play_count',
                sortType: 'play_count',

                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Likes',
                accessor: 'like_count',
                sortType: 'like_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Comments',
                accessor: 'comment_count',
                sortType: 'comment_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Shares',
                accessor: 'share_count',
                sortType: 'share_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Saves',
                accessor: 'save_count',
                Cell: ({ value }) => (
                    <>{loading ? <Skeleton /> : typeof value === 'number' ? numberWithCommas(value) : '-'}</>
                ),
            },
        ],
        [loading]
    );

    return (
        <PaginationTable
            manualPagination
            manualSortBy
            disableSortBy={false}
            data={rows}
            columns={columns}
            page={page}
            pageSize={pageSize}
            setPageSize={onPageSizeChange}
            setPage={onPageChange}
            orderBy={orderBy}
            onOrderByChange={onOrderByChange}
            loading={loading}
            count={count}
            noDataLabel={noDataLabel}
            tableClassName={styles.table}
            theadClassName={styles.thead}
            tbodyClassName={styles.tbody}
        />
    );
};

export default ClientTopPostsTable;
